/**
 * Movement breadcrumbs (FE-30 P2).
 *
 * Renders the last few hex positions each mech occupied as side-colored
 * discs on the floor, opacity ramping by rank so the freshest crumb is
 * brightest and oldest is most translucent. Pure function of
 * `(battle, currentTick)` — scrubbing the timeline rewinds the trail.
 *
 * The actual trail-collection lives in `./movementTrail.ts` as plain
 * TS so it unit-tests without R3F.
 */

import { useMemo } from "react";
import type { ParsedBattle } from "@pilot-protocol/replay-parser";
import { hexToWorld, TILE_THICKNESS } from "./hexMath";
import { buildTrail, BREADCRUMB_TRAIL_LENGTH, type Breadcrumb } from "./movementTrail";

export interface MovementTrailsProps {
  battle: ParsedBattle;
  currentTick: number;
  colorA: string;
  colorB: string;
  /** Cap on breadcrumbs per side. Defaults to `BREADCRUMB_TRAIL_LENGTH = 4`. */
  trailLength?: number;
}

// Disc radius is slightly smaller than the hex tile so the breadcrumb
// reads as a "footstep" mark rather than overlapping neighbors.
const BREADCRUMB_RADIUS = 0.42;
const BREADCRUMB_HEIGHT = 0.04;
const BREADCRUMB_BASE_OPACITY = 0.55;
const FLOOR_OFFSET = TILE_THICKNESS + 0.01;

export function MovementTrails({
  battle,
  currentTick,
  colorA,
  colorB,
  trailLength = BREADCRUMB_TRAIL_LENGTH,
}: MovementTrailsProps) {
  const trailA = useMemo(
    () => buildTrail(battle, currentTick, "A", trailLength),
    [battle, currentTick, trailLength],
  );
  const trailB = useMemo(
    () => buildTrail(battle, currentTick, "B", trailLength),
    [battle, currentTick, trailLength],
  );

  return (
    <group name="movement-trails">
      {trailA.map((b) => (
        <TrailMark
          key={`A-${b.pos.q}-${b.pos.r}-${b.rank}`}
          crumb={b}
          color={colorA}
          maxRank={trailLength}
        />
      ))}
      {trailB.map((b) => (
        <TrailMark
          key={`B-${b.pos.q}-${b.pos.r}-${b.rank}`}
          crumb={b}
          color={colorB}
          maxRank={trailLength}
        />
      ))}
    </group>
  );
}

interface TrailMarkProps {
  crumb: Breadcrumb;
  color: string;
  maxRank: number;
}

function TrailMark({ crumb, color, maxRank }: TrailMarkProps) {
  const w = hexToWorld(crumb.pos, 0);
  // rank 1 (freshest past) → full base opacity. rank N (oldest) →
  // 1/N of base opacity. Linear ramp keeps the visual "fade" intuitive.
  const opacity = BREADCRUMB_BASE_OPACITY * Math.max(0, 1 - (crumb.rank - 1) / maxRank);
  return (
    <mesh position={[w.x, FLOOR_OFFSET, w.z]}>
      <cylinderGeometry args={[BREADCRUMB_RADIUS, BREADCRUMB_RADIUS, BREADCRUMB_HEIGHT, 6]} />
      <meshBasicMaterial color={color} transparent opacity={opacity} depthWrite={false} />
    </mesh>
  );
}
